import { useState, useCallback, useEffect, useMemo } from 'react';
import { IGridProps } from '@components-library/Grid/interfaces';
import { ColumnOrderState } from '@tanstack/react-table';
import { gridContextPersistenceService } from './gridContextPersistence.service';

export const useColumnOrder = ({
  gridName,
  columns = [],
}: {
  gridName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: IGridProps<any, any>['columns'];
}) => {
  useEffect(() => {
    if (columns.length > 0) {
      columns.forEach((column) => {
        if (!(column as { accessorKey: string })?.accessorKey) {
          throw new Error('Column is missing accessorKey');
        }
      });
    }
  }, [columns]);

  const defaultOrderState = useMemo(
    () =>
      columns.map((column) => {
        /** @description React Table replaces dots with underscores in the accessorKey, so we need to replace them manually */
        const fallbackId = ((column as { accessorKey: string })?.accessorKey ?? '').split('.').join('_');

        return column?.id ?? fallbackId;
      }),
    [columns],
  );

  const persistedOrderState = !gridName
    ? null
    : gridContextPersistenceService.getGridPersistanceStorage(gridName)?.order;

  const [columnOrder, setColumnOrder] = useState<string[]>(
    !persistedOrderState ? [] : mergeWithOldColumnOrder(defaultOrderState, persistedOrderState),
  );

  const handleSetColumnOrder = useCallback(
    (updatedColumnOrderState: ColumnOrderState) => {
      if (gridName) {
        gridContextPersistenceService.saveOrderToGridPersistanceStorage(gridName, updatedColumnOrderState);
      }
      setColumnOrder(updatedColumnOrderState);
    },
    [gridName],
  );

  return { columnOrder, setColumnOrder: handleSetColumnOrder };
};

export const mergeWithOldColumnOrder = (newColumnOrder: string[], oldColumnOrder: string[]) => {
  let resultOrder = oldColumnOrder;

  resultOrder = resultOrder.map((item) => {
    if (newColumnOrder.includes(item)) {
      return item;
    } else {
      return '';
    }
  });

  newColumnOrder.forEach((item, index) => {
    if (resultOrder.includes(item)) {
      return;
    } else {
      resultOrder.splice(index, 0, item);
      resultOrder = findAndRemoveFirstEmpty(resultOrder);
    }
  });

  resultOrder = resultOrder.filter((item) => !!item);

  return resultOrder;
};

const findAndRemoveFirstEmpty = <T>(arr: T[]) => {
  const firstEmptyIndex = arr.indexOf('' as T);

  if (firstEmptyIndex === -1) {
    return arr;
  }

  return arr.slice(0, firstEmptyIndex).concat(arr.slice(firstEmptyIndex + 1));
};
